import React, { useEffect, useState } from 'react';

import { LinkButton } from 'src/app/components/lib/button';
import { MultiFilterSelect } from 'src/app/components/lib/multi-filter-select';

import { getMultiSelectedOptionsGeneric, parseSelectedValuesGeneric } from './utils';

import type { DropdownMenuOption } from 'src/app/components/lib/dropdown';

type DataItem = {
  [key: string]: any;
  id: number | string;
  name: string;
};

export type GenericMultiSelectProps<T extends DataItem> = {
  automationIdPrefix: string;
  defaultLabel?: string;
  disabled?: boolean;
  idKey?: keyof T;
  items: T[];
  loading?: boolean;
  maxDisplayedItems?: number;
  nameKey?: keyof T;
  onChange: (value: (number | string)[]) => void;
  refreshData?: () => Promise<void>;
  values: string;
};

export function QueryBuilderMultiSelect<T extends DataItem>({
  items,
  values,
  onChange,
  automationIdPrefix,
  disabled = false,
  defaultLabel = 'Select items',
  idKey = 'id' as keyof T,
  nameKey = 'name' as keyof T,
  refreshData,
  loading = false,
  maxDisplayedItems = 3,
}: GenericMultiSelectProps<T>) {
  const [label, setLabel] = useState<string>(defaultLabel);
  const [dropdownSelection, setDropdownSelection] = useState<DropdownMenuOption[]>([]);

  const getMultiSelectedOptionsForItems = (values: string, allItems: T[]): DropdownMenuOption[] =>
    getMultiSelectedOptionsGeneric(
      values,
      allItems,
      parseSelectedValuesGeneric,
      (option) => option[idKey],
      (option) => ({
        label: String(option[nameKey]),
        id: String(option[idKey]),
      })
    );

  useEffect(() => {
    if (dropdownSelection.length !== 0 && dropdownSelection.length <= maxDisplayedItems) {
      const labelList = dropdownSelection.map((item) => item.label).join(', ');
      setLabel(labelList);
    } else if (dropdownSelection.length > maxDisplayedItems) {
      setLabel(`${dropdownSelection.length} items selected`);
    } else {
      setLabel(defaultLabel);
    }
  }, [dropdownSelection, defaultLabel, maxDisplayedItems]);

  useEffect(() => {
    // Update selection when items or values change
    setDropdownSelection(getMultiSelectedOptionsForItems(values, items));
  }, [items, values]);

  const handleFilterChange = (values: DropdownMenuOption[]) => {
    setDropdownSelection(values);
    const selectedIds = values.map((option) => option.id);
    onChange(selectedIds);
  };

  return (
    <MultiFilterSelect
      activeFilter={dropdownSelection}
      automationId={`${automationIdPrefix}_multi-filter-select`}
      disabled={disabled}
      footerContent={
        refreshData && (
          <LinkButton
            automationId={`${automationIdPrefix}_multi-filter-select_refresh-button`}
            buttonSize='small'
            key={`${automationIdPrefix}-refresh`}
            label='Refresh'
            loading={loading}
            onClick={refreshData}
          />
        )
      }
      label={label}
      options={items.map((item) => ({
        label: String(item[nameKey]),
        id: String(item[idKey]),
      }))}
      onChange={handleFilterChange}
    />
  );
}
